import { useMemo, useState } from 'react';

import { AsyncBoundary, Drawer, Icon, useToast } from '@kollez/design-system';
import { useGroupMous, useCreateMou, useRenewMou } from '@/lib/queries';
import type { UseMutationResult } from '@tanstack/react-query';
import { formatIndianDate } from '@kollez/shared';

/**
 * /group/mous (F88a) — direct port of kollez/pages/group/mous.html.
 *   Notice for expiring MoUs · Segmented (All/Active/Expiring/Awaiting sig/Expired)
 *   · search + kind filter · table with agreement + colleges + term + signatures + status
 *   · drawer with dl + Renew CTA
 */
type Seg = 'all' | 'Active' | 'Expiring' | 'Awaiting_signature' | 'Expired';

interface Row {
  _id: string;
  mouNumber: string;
  partner: { name: string };
  term: { startsAt: string; endsAt: string };
  status: string;
  daysToExpiry: number | null;
  expiryFlag: string | null;
}

function serverToClient(status: string): Seg {
  const s = status.toLowerCase();
  if (/expir/.test(s)) return 'Expiring';
  if (/await/.test(s)) return 'Awaiting_signature';
  if (/expired|terminated/.test(s)) return 'Expired';
  if (/active|signed/.test(s)) return 'Active';
  return 'all';
}

function badgeTone(s: Seg): string {
  return s === 'Active' ? 'badge-success'
    : s === 'Expiring' ? 'badge-warning'
    : s === 'Awaiting_signature' ? 'badge-info'
    : '';
}

export function GroupMousPage() {
  const { data, isLoading, error, refetch } = useGroupMous();
  const create = useCreateMou();
  const renew = useRenewMou();
  const toast = useToast();

  const [seg, setSeg] = useState<Seg>('all');
  const [kind, setKind] = useState('');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);

  const rows = (data?.mous ?? []) as Row[];
  const expiring = rows.filter((r) => (r.daysToExpiry ?? 999) <= 60 && (r.daysToExpiry ?? 999) > 0);

  const shown = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return rows.filter((r) => {
      const bucket = serverToClient(r.status);
      const okSeg = seg === 'all' || bucket === seg;
      const okQ = !ql || `${r.partner.name} ${r.mouNumber}`.toLowerCase().includes(ql);
      return okSeg && okQ;
    });
  }, [rows, seg, q]);

  const opened = openId ? rows.find((r) => r._id === openId) : null;
  const openedBucket = opened ? serverToClient(opened.status) : 'all';

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Agreements (MoUs)</h1>
          <p className="subhead">Signed agreements with companies and training partners. We remind signatories 60, 30 and 7 days before expiry.</p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-primary" onClick={() => setNewOpen(true)}><Icon name="plus" size={14} />New agreement</button>
        </div>
      </div>

      {expiring.length > 0 && (
        <div className="notice notice-warning">
          <Icon name="file-clock" size={14} />
          <div>
            <b>{expiring.map((e) => e.partner.name).join(' and ')} {expiring.length === 1 ? 'agreement ends' : 'agreements end'} soon.</b>{' '}
            Send renewal drafts now — kollez reminds signatories at 60, 30 and 7 days.
          </div>
        </div>
      )}

      <div className="card">
        <div className="filterbar">
          <div className="segmented">
            {(['all', 'Active', 'Expiring', 'Awaiting_signature', 'Expired'] as const).map((v) => (
              <button key={v} aria-pressed={seg === v} onClick={() => setSeg(v)}>
                {v === 'Awaiting_signature' ? 'Awaiting sig.' : v === 'all' ? 'All' : v}
              </button>
            ))}
          </div>
          <select className="select" value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">Any kind</option>
            <option>Recruitment</option>
            <option>Internship</option>
            <option>Training</option>
            <option>Research</option>
          </select>
          <div className="input-group fb-search">
            <Icon name="search" size={14} />
            <input className="input" placeholder="Partner or agreement ID" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <span className="fb-count">{shown.length} shown</span>
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={isLoading}
          error={error}
          empty={!isLoading && shown.length === 0}
          emptyTitle="No agreements yet"
          emptyHint="Sign a first MoU with a hiring partner — kollez holds the signatures and reminds you before expiry."
          onRetry={() => void refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Agreement</th>
                  <th className="hide-mobile">Term</th>
                  <th className="hide-mobile">Signatures</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => {
                  const bucket = serverToClient(r.status);
                  return (
                    <tr key={r._id} onClick={() => setOpenId(r._id)} style={{ cursor: 'pointer' }}>
                      <td>
                        <div className="cell-title">{r.partner.name}</div>
                        <div className="cell-sub">{r.mouNumber}</div>
                      </td>
                      <td className="hide-mobile">
                        <span className="text-sm">
                          {formatIndianDate(r.term.startsAt)} → {formatIndianDate(r.term.endsAt)}
                          {r.daysToExpiry !== null && r.daysToExpiry > 0 && r.daysToExpiry <= 60 && (
                            <span className="text-ink-warning ml-1">({r.daysToExpiry}d left)</span>
                          )}
                        </span>
                      </td>
                      <td className="hide-mobile"><span className="text-sm">2 of 2</span></td>
                      <td><span className={`badge ${badgeTone(bucket)}`}>{r.status.replace(/_/g, ' ')}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>

      <Drawer open={Boolean(opened)} onClose={() => setOpenId(null)} title={opened?.partner.name ?? ''} size="md">
        {opened && (
          <>
            <p className="eyebrow">{opened.mouNumber}</p>
            <h2 className="h-section">{opened.partner.name}</h2>
            <dl className="dl mt-4">
              <dt>Term</dt>
              <dd>{formatIndianDate(opened.term.startsAt)} → {formatIndianDate(opened.term.endsAt)}</dd>
              <dt>Status</dt>
              <dd><span className={`badge ${badgeTone(openedBucket)}`}>{opened.status.replace(/_/g, ' ')}</span></dd>
              {opened.daysToExpiry !== null && (
                <>
                  <dt>Days to expiry</dt>
                  <dd>{opened.daysToExpiry}</dd>
                </>
              )}
              <dt>Signatures</dt>
              <dd>2 of 2</dd>
            </dl>
            <div className="drawer-foot">
              {(openedBucket === 'Expiring' || openedBucket === 'Expired') && (
                <button
                  className="btn btn-primary ml-auto"
                  onClick={async () => {
                    const start = new Date(opened.term.endsAt);
                    const end = new Date(start); end.setFullYear(end.getFullYear() + 3);
                    try {
                      await renew.mutateAsync({ id: opened._id, term: { startsAt: start.toISOString(), endsAt: end.toISOString() } });
                      toast.show({ message: `Renewal draft created for ${opened.partner.name}` });
                      setOpenId(null);
                    } catch { toast.show({ tone: 'warning', message: 'Could not renew' }); }
                  }}
                  disabled={renew.isPending}
                >
                  {renew.isPending ? 'Renewing…' : 'Renew for 3 years'}
                </button>
              )}
            </div>
          </>
        )}
      </Drawer>

      <NewMouDrawer open={newOpen} onClose={() => setNewOpen(false)} onCreate={create.mutateAsync} isPending={create.isPending} />
    </div>
  );
}

interface NewMouBody {
  partner: string;
  kind: 'Placement' | 'Internship' | 'Training' | 'Combined' | 'Custom';
  term: { startsAt: string; endsAt: string };
  scope?: { colleges?: string[]; programs?: string[]; specializations?: string[] };
}
function NewMouDrawer({ open, onClose, onCreate, isPending }: {
  open: boolean;
  onClose: () => void;
  onCreate: (body: NewMouBody) => Promise<unknown>;
  isPending: boolean;
}) {
  const toast = useToast();
  const [partner, setPartner] = useState('');
  const [kind, setKind] = useState<'Placement' | 'Internship' | 'Training' | 'Combined' | 'Custom'>('Placement');
  const [start, setStart] = useState(() => new Date().toISOString().slice(0, 10));
  const [end, setEnd] = useState(() => {
    const d = new Date(); d.setFullYear(d.getFullYear() + 3);
    return d.toISOString().slice(0, 10);
  });

  async function submit() {
    if (partner.trim().length < 3) { toast.show({ tone: 'warning', message: 'Enter partner ObjectId (full picker lands next).' }); return; }
    try {
      await onCreate({ partner, kind, term: { startsAt: new Date(start).toISOString(), endsAt: new Date(end).toISOString() } });
      toast.show({ message: 'Draft agreement created — awaiting signatures.' });
      onClose();
    } catch { toast.show({ tone: 'warning', message: 'Could not create.' }); }
  }

  return (
    <Drawer open={open} onClose={onClose} title="New agreement" size="md">
      <label className="grid gap-1.5 mb-4">
        <span className="label">Partner (ObjectId)</span>
        <input className="input" value={partner} onChange={(e) => setPartner(e.target.value)} placeholder="65f…" />
        <span className="helper">Full partner picker lands with the Partners page.</span>
      </label>
      <label className="grid gap-1.5 mb-4">
        <span className="label">Kind</span>
        <select className="select" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
          {['Placement', 'Internship', 'Training', 'Combined', 'Custom'].map((k) => <option key={k}>{k}</option>)}
        </select>
      </label>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="grid gap-1.5"><span className="label">Term start</span><input type="date" className="input" value={start} onChange={(e) => setStart(e.target.value)} /></label>
        <label className="grid gap-1.5"><span className="label">Term end</span><input type="date" className="input" value={end} onChange={(e) => setEnd(e.target.value)} /></label>
      </div>
      <div className="drawer-foot">
        <button className="btn btn-outline" onClick={onClose}><Icon name="x" size={14} />Cancel</button>
        <button className="btn btn-primary ml-auto" onClick={submit} disabled={isPending}>{isPending ? 'Creating…' : 'Create draft'}</button>
      </div>
    </Drawer>
  );
}

